import React, {useMemo} from 'react'
import {
	Box,
	Card,
	CardContent,
	Chip,
	Divider,
	Grid,
	IconButton,
	Skeleton,
	Stack,
	Tooltip,
	Typography
} from '@mui/material'
import CameraAltIcon from '@mui/icons-material/CameraAlt'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import PhotoIcon from '@mui/icons-material/Photo'
import TodayIcon from '@mui/icons-material/Today'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import RefreshIcon from '@mui/icons-material/Refresh'
import {useGetCamerasDashboardQuery} from '@containers/cameras/queryHooks/useCamerasQuery'
import {ICameraDashboardCamera, ICameraDashboardDevice} from '@containers/cameras/types'
import {CAMERA_POSITION_LABELS} from '@containers/cameras/select/selectCameraPosition/constants'
import moment from 'moment'

interface IStatCard {
	icon: React.ReactNode
	label: string
	value: number | string
	color: string
	bg: string
}

const isCameraRecent = (camera: ICameraDashboardCamera) =>
	camera.lastPhotoAt ? moment().diff(moment(camera.lastPhotoAt), 'minutes') <= 60 : false

const isCameraOnline = (camera: ICameraDashboardCamera) =>
	camera.lastPhotoAt ? moment().diff(moment(camera.lastPhotoAt), 'minutes') <= 15 : false

type CameraStatus = {
	label: string
	color: 'success' | 'warning' | 'error'
	timeColor: string
}

const getCameraStatus = (camera: ICameraDashboardCamera): CameraStatus => {
	if (!camera.lastPhotoAt) {
		return {label: 'Офлайн', color: 'error', timeColor: 'error.main'}
	}
	if (isCameraOnline(camera)) {
		return {label: 'Онлайн', color: 'success', timeColor: 'success.main'}
	}
	return {label: 'Офлайн', color: 'warning', timeColor: 'warning.main'}
}

const StatCard = ({icon, label, value, color, bg}: IStatCard) => (
	<Card variant="outlined" sx={{flex: 1, minWidth: 140}}>
		<CardContent sx={{p: 2, '&:last-child': {pb: 2}}}>
			<Stack direction="row" alignItems="center" spacing={1.5}>
				<Box
					sx={{
						width: 40,
						height: 40,
						borderRadius: 2,
						bgcolor: bg,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						color,
						flexShrink: 0,
					}}
				>
					{icon}
				</Box>
				<Box>
					<Typography variant="h5" fontWeight={700} lineHeight={1.1}>
						{value}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						{label}
					</Typography>
				</Box>
			</Stack>
		</CardContent>
	</Card>
)

const CameraBlock = ({camera}: {camera: ICameraDashboardCamera}) => {
	const lastPhoto = camera.lastPhotoAt
		? moment(camera.lastPhotoAt).format('DD.MM.YYYY HH:mm')
		: '—'
	const fromNow = camera.lastPhotoAt ? moment(camera.lastPhotoAt).fromNow() : null
	const status = getCameraStatus(camera)

	return (
		<Box>
			<Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.75}>
				<Stack direction="row" alignItems="center" spacing={0.75} minWidth={0}>
					<CameraAltIcon sx={{color: 'text.secondary', fontSize: 17}}/>
					<Box minWidth={0}>
						<Stack direction="row" alignItems="center" spacing={0.75}>
							<Typography variant="subtitle2" fontWeight={700} noWrap>
								{camera.cameraId}
							</Typography>
							<Chip
								size="small"
								icon={status.color === 'success'
									? <CheckCircleIcon sx={{fontSize: '14px !important'}}/>
									: <CancelIcon sx={{fontSize: '14px !important'}}/>
								}
								label={status.label}
								color={status.color}
								sx={{
									height: 20,
									fontSize: 11,
									fontWeight: 700,
									'& .MuiChip-icon': {color: 'inherit'},
								}}
							/>
						</Stack>
						<Typography variant="caption" color="text.secondary" noWrap display="block">
							Позиція: {camera.cameraPosition
								? (CAMERA_POSITION_LABELS[camera.cameraPosition] ?? camera.cameraPosition)
								: 'невідома'}
							{camera.cameraType ? ` · ${camera.cameraType}` : ''}
						</Typography>
					</Box>
				</Stack>
				<Stack direction="column" alignItems="flex-end" spacing={0.5} flexShrink={0}>
					<Chip
						size="small"
						icon={camera.isActive
							? <CheckCircleIcon sx={{fontSize: '14px !important'}}/>
							: <CancelIcon sx={{fontSize: '14px !important'}}/>
						}
						label={camera.isActive ? 'Фото увімк.' : 'Фото вимк.'}
						color={camera.isActive ? 'success' : 'default'}
						variant="outlined"
						sx={{height: 22, fontSize: 11, minWidth: 110}}
					/>
					<Chip
						size="small"
						icon={camera.zoneRecordingMode
							? <CheckCircleIcon sx={{fontSize: '14px !important'}}/>
							: <CancelIcon sx={{fontSize: '14px !important'}}/>
						}
						label={camera.zoneRecordingMode ? 'Запис увімк.' : 'Запис вимк.'}
						color={camera.zoneRecordingMode ? 'success' : 'default'}
						variant="outlined"
						sx={{height: 22, fontSize: 11, minWidth: 110}}
					/>
				</Stack>
			</Stack>

			<Stack spacing={0.5}>
				<Stack direction="row" alignItems="center" spacing={0.5}>
					<TodayIcon sx={{fontSize: 14, color: 'primary.main'}}/>
					<Typography variant="caption" color="text.secondary">
						Сьогодні надіслано:
					</Typography>
					<Typography variant="caption" fontWeight={700}>
						{camera.todayPhotos} фото
					</Typography>
				</Stack>
				<Stack direction="row" alignItems="center" spacing={0.5}>
					<PhotoIcon sx={{fontSize: 14, color: 'text.secondary'}}/>
					<Typography variant="caption" color="text.secondary">
						За 30 днів:
					</Typography>
					<Typography variant="caption" fontWeight={700} color="text.secondary">
						{camera.monthPhotos.toLocaleString()} фото
					</Typography>
				</Stack>
				<Stack direction="row" alignItems="center" spacing={0.5}>
					<AccessTimeIcon sx={{fontSize: 14, color: 'text.secondary'}}/>
					<Typography variant="caption" color="text.secondary">
						Останнє фото:
					</Typography>
					{camera.lastPhotoAt ? (
						<Tooltip title={lastPhoto}>
							<Typography
								variant="caption"
								fontWeight={700}
								color={status.timeColor}
							>
								{fromNow}
							</Typography>
						</Tooltip>
					) : (
						<Typography variant="caption" fontWeight={700} color="text.disabled">
							ще не було фото
						</Typography>
					)}
				</Stack>
			</Stack>
		</Box>
	)
}

const DeviceCard = ({device}: {device: ICameraDashboardDevice}) => {
	const cameras = device.cameras ?? []
	const hasNoPhotos = cameras.length > 0 && cameras.every((c) => c.monthPhotos === 0)
	const isRecent = cameras.some(isCameraRecent)

	const bgColor = hasNoPhotos ? '#fff5f5' : isRecent ? '#f1f8f1' : 'background.paper'

	return (
		<Card
			variant="outlined"
			sx={{
				height: '100%',
				transition: 'box-shadow 0.2s',
				'&:hover': {boxShadow: 3},
				borderLeft: '3px solid',
				borderLeftColor: isRecent ? 'success.main' : 'grey.400',
				bgcolor: bgColor,
			}}
		>
			<CardContent sx={{p: 2, '&:last-child': {pb: 2}}}>
				<Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
					<Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
						<DirectionsCarIcon sx={{color: 'primary.main', fontSize: 20}}/>
						<Typography variant="subtitle1" fontWeight={700} noWrap>
							{device.licencePlate || `Девайс ${device.deviceId}`}
						</Typography>
					</Stack>
					<Chip
						size="small"
						label={`${cameras.length} ${cameras.length === 1 ? 'камера' : 'камер'}`}
						variant="outlined"
						sx={{height: 22, fontSize: 11, flexShrink: 0}}
					/>
				</Stack>

				<Stack
					divider={<Divider flexItem/>}
					spacing={1.5}
				>
					{cameras.map((camera) => (
						<CameraBlock key={`${device.deviceId}-${camera.cameraId}`} camera={camera}/>
					))}
				</Stack>
			</CardContent>
		</Card>
	)
}

const SkeletonCard = () => (
	<Card variant="outlined">
		<CardContent sx={{p: 2, '&:last-child': {pb: 2}}}>
			<Stack direction="row" justifyContent="space-between" mb={1.5}>
				<Skeleton variant="text" width={120} height={26}/>
				<Skeleton variant="rounded" width={70} height={22}/>
			</Stack>
			<Skeleton variant="text" width="70%" height={20}/>
			<Skeleton variant="text" width="50%" height={18}/>
			<Stack direction="row" spacing={1.5} mt={1}>
				<Skeleton variant="text" width={60} height={18}/>
				<Skeleton variant="text" width={70} height={18}/>
			</Stack>
		</CardContent>
	</Card>
)

const CamerasDashboard = ({accountId}: {accountId: number}) => {
	const {data, isLoading, refetch} = useGetCamerasDashboardQuery(accountId)

	const stats = useMemo(() => {
		if (!data) return null
		const cameras = data.flatMap((d) => d.cameras ?? [])
		const lastHour = cameras.filter(isCameraRecent).length
		const todayTotal = cameras.reduce((sum, c) => sum + c.todayPhotos, 0)
		return {
			vehicles: data.length,
			cameras: cameras.length,
			lastHour,
			todayTotal,
		}
	}, [data])

	return (
		<Box>
			<Stack direction="row" justifyContent="flex-end" mb={1}>
				<Tooltip title="Оновити">
					<IconButton size="small" onClick={() => refetch()} disabled={isLoading}>
						<RefreshIcon fontSize="small"/>
					</IconButton>
				</Tooltip>
			</Stack>
			<Stack direction="row" flexWrap="wrap" gap={2} mb={3}>
				{isLoading || !stats
					? Array.from({length: 4}).map((_, i) => (
						<Card key={i} variant="outlined" sx={{flex: 1, minWidth: 140}}>
							<CardContent sx={{p: 2, '&:last-child': {pb: 2}}}>
								<Stack direction="row" spacing={1.5} alignItems="center">
									<Skeleton variant="rounded" width={40} height={40}/>
									<Box>
										<Skeleton variant="text" width={50} height={32}/>
										<Skeleton variant="text" width={80} height={16}/>
									</Box>
								</Stack>
							</CardContent>
						</Card>
					))
					: <>
						<StatCard
							icon={<DirectionsCarIcon fontSize="small"/>}
							label="Всього авто"
							value={stats.vehicles}
							color="#1976d2"
							bg="#e3f2fd"
						/>
						<StatCard
							icon={<CameraAltIcon fontSize="small"/>}
							label="Всього камер"
							value={stats.cameras}
							color="#1565c0"
							bg="#e3f2fd"
						/>
						<StatCard
							icon={<CheckCircleIcon fontSize="small"/>}
							label="Камер слали фото < 1 год"
							value={stats.lastHour}
							color="#2e7d32"
							bg="#e8f5e9"
						/>
						<StatCard
							icon={<TodayIcon fontSize="small"/>}
							label="Фото надіслано сьогодні"
							value={stats.todayTotal.toLocaleString()}
							color="#e65100"
							bg="#fff3e0"
						/>
					</>
				}
			</Stack>

			<Grid container spacing={2}>
				{isLoading
					? Array.from({length: 8}).map((_, i) => (
						<Grid item xs={12} sm={6} md={4} lg={3} key={i}>
							<SkeletonCard/>
						</Grid>
					))
					: data?.map((device) => (
						<Grid item xs={12} sm={6} md={4} lg={3} key={device.deviceId}>
							<DeviceCard device={device}/>
						</Grid>
					))
				}
			</Grid>

			{!isLoading && !data?.length && (
				<Box sx={{textAlign: 'center', py: 8, color: 'text.secondary'}}>
					<CameraAltIcon sx={{fontSize: 48, mb: 1, opacity: 0.3}}/>
					<Typography variant="body2">Камер не знайдено</Typography>
				</Box>
			)}
		</Box>
	)
}

export default CamerasDashboard
